import { createBrowserClient } from "@supabase/ssr";
import { Capacitor } from "@capacitor/core";
import { createRateLimitAwareAuthFetch } from "@/lib/supabase/auth-refresh-rate-limit";
import { getBrowserStorageAdapter } from "@/lib/supabase/browser-storage";

// Singleton client instance for the browser
let browserClient: ReturnType<typeof createBrowserClient> | null = null;

export function createClient() {
  if (browserClient) {
    return browserClient;
  }

  const isNative = typeof window !== "undefined" && Capacitor.isNativePlatform();

  browserClient = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      global: {
        fetch: createRateLimitAwareAuthFetch(),
      },
      ...(isNative
        ? {}
        : {
            cookies: {
              getAll() {
                const parsed: { name: string; value: string }[] = [];
                for (const entry of document.cookie.split(";")) {
                  const eq = entry.indexOf("=");
                  if (eq < 0) continue;
                  const name = entry.slice(0, eq).trim();
                  const value = entry.slice(eq + 1);
                  if (name) parsed.push({ name, value });
                }
                return parsed;
              },
              setAll(cookiesToSet) {
                for (const { name, value, options } of cookiesToSet) {
                  let cookieStr = `${name}=${value}; path=/`;
                  if (options?.maxAge) cookieStr += `; max-age=${options.maxAge}`;
                  if (options?.domain) cookieStr += `; domain=${options.domain}`;
                  if (options?.sameSite) {
                    const s = typeof options.sameSite === "string" ? options.sameSite.toLowerCase() : "lax";
                    cookieStr += `; samesite=${s}`;
                  }
                  if (options?.secure) cookieStr += "; secure";
                  document.cookie = cookieStr;
                }
              },
            },
          }),
      auth: {
        ...(isNative
          ? {
              storage: getBrowserStorageAdapter(),
              persistSession: true,
              autoRefreshToken: true,
              detectSessionInUrl: false,
            }
          : {
              autoRefreshToken: false,
              detectSessionInUrl: false,
            }),
      },
    }
  );

  if (!isNative) {
    void browserClient.auth.stopAutoRefresh().catch((error: unknown) => {
      console.warn("[Supabase] Failed to stop browser auth auto-refresh", {
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }

  return browserClient;
}

/**
 * Reset the client (useful for testing or after sign out)
 */
export function resetClient() {
  browserClient = null;
}
